import React, { useState, useEffect, useRef } from 'react';
import { paymentsApi, paymentOrdersApi } from '../../../../api/bankApi';
import { getApiErrorText } from '../../../../utils/apiResponse';
import privatbankIcon from '../../../../assets/images/privatbank.png';
import monobankIcon from '../../../../assets/images/monobank.png';

const AttachPaymentToOrder = ({ orderId, orderPrice, orderPaymentStatus, onPaymentAttached, refreshKey = 0, isMobile = false }) => {
  const availableRequest = useRef(0);
  const attachedRequest = useRef(0);
  const [availablePayments, setAvailablePayments] = useState([]);
  const [attachedPayments, setAttachedPayments] = useState([]);
  const [paymentsOpened, setPaymentsOpened] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDetachDropdown, setShowDetachDropdown] = useState({}); // Map paymentOrderId -> boolean
  const [selectedPaymentOrderToDetach, setSelectedPaymentOrderToDetach] = useState(null);
  const [detachDropdownPosition, setDetachDropdownPosition] = useState({}); // Map paymentOrderId -> { top, right }
  const [showCreateManualPaymentModal, setShowCreateManualPaymentModal] = useState(false);
  const [createManualError, setCreateManualError] = useState('');
  const [manualPaymentForm, setManualPaymentForm] = useState({
    amount: '',
    adminComment: ''
  });

  useEffect(() => {
    if (orderId) {
      loadAttachedPayments();
      loadAvailablePayments();
    }
    return () => {
      availableRequest.current += 1;
      attachedRequest.current += 1;
    };
  }, [orderId, orderPrice, orderPaymentStatus?.databaseValue, refreshKey]);

  // Закриваємо dropdown при кліку поза ним
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.position-relative')) {
        setShowDropdown(false);
      }
      
      const hasOpenDetachDropdown = Object.values(showDetachDropdown).some(v => v === true);
      if (hasOpenDetachDropdown) {
        const dropdownContainer = event.target.closest('.payment-detach-dropdown-container');
        if (!dropdownContainer) {
          setShowDetachDropdown({});
          setSelectedPaymentOrderToDetach(null);
        }
      }
    };

    if (showDropdown || Object.values(showDetachDropdown).some(v => v === true)) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showDropdown, showDetachDropdown]);

  const loadAvailablePayments = async () => {
    const request = ++availableRequest.current;
    setIsLoading(true);
    try {
      // Завантажуємо оплати зі статусами PENDING та PARTIALLY_USED
      // Важливо: PARTIALLY_USED оплати можна прикріплювати до замовлень до тих пір,
      // поки є доступна сума (availableAmount > 0). Після повного використання
      // оплата переходить у статус USED і більше не може бути прикріплена.
      const [pendingRes, partiallyUsedRes] = await Promise.all([
        paymentsApi.getAll({
          status: 'PENDING',
          type: 'IN', // Тільки вхідні оплати
          numberPerPage: 1000
        }),
        paymentsApi.getAll({
          status: 'PARTIALLY_USED',
          type: 'IN', // Тільки вхідні оплати
          numberPerPage: 1000
        })
      ]);
      
      const allPayments = [];
      if (pendingRes.status === 200 && pendingRes.data.data) {
        allPayments.push(...pendingRes.data.data);
      }
      if (partiallyUsedRes.status === 200 && partiallyUsedRes.data.data) {
        // Фільтруємо PARTIALLY_USED оплати, щоб залишити тільки ті, що мають availableAmount > 0
        // Це дозволяє продовжувати прикріплювати частково використані оплати до нових замовлень
        const partiallyUsedWithAmount = partiallyUsedRes.data.data.filter(
          p => {
            const status = p.status?.databaseValue || p.status;
            return status === 'PARTIALLY_USED' && p.availableAmount && parseFloat(p.availableAmount) > 0;
          }
        );
        allPayments.push(...partiallyUsedWithAmount);
      }
      
      if (request === availableRequest.current) setAvailablePayments(allPayments);
    } catch (error) {
      console.error('Error loading available payments:', error);
    } finally {
      if (request === availableRequest.current) setIsLoading(false);
    }
  };

  const loadAttachedPayments = async () => {
    const request = ++attachedRequest.current;
    try {
      const res = await paymentOrdersApi.getOrderAttachments(orderId);
      if (request === attachedRequest.current && res.status === 200 && res.data.data) {
        setAttachedPayments(res.data.data);
      }
    } catch (error) {
      console.error('Error loading attached payments:', error);
    }
  };

  const handleAttachPayment = async () => {
    if (!selectedPaymentId) {
      setErrorMessage('Будь ласка, виберіть оплату');
      return;
    }

    const selectedPayment = availablePayments.find(p => p.id === parseInt(selectedPaymentId));
    if (!selectedPayment) {
      setErrorMessage('Оплату не знайдено');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      // Прикріплюємо всю суму оплати
      await paymentOrdersApi.attach(selectedPayment.id, orderId, null);
      setSuccessMessage('Оплату успішно прикріплено');
      setSelectedPaymentId(null);
      setSearchQuery('');
      setShowDropdown(false);
      await loadAttachedPayments();
      await loadAvailablePayments(); // Оновлюємо список доступних оплат
      if (onPaymentAttached) {
        onPaymentAttached();
      }
      setTimeout(() => {
        setSuccessMessage('');
      }, 2000);
    } catch (error) {
      setErrorMessage(getApiErrorText(error, 'Помилка прикріплення оплати'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDetachDropdown = (paymentOrder, event) => {
    setSelectedPaymentOrderToDetach(paymentOrder);
    
    // Обчислюємо позицію кнопки відносно viewport для position: fixed
    if (event && event.currentTarget) {
      const buttonRect = event.currentTarget.getBoundingClientRect();
      setDetachDropdownPosition(prev => ({
        ...prev,
        [paymentOrder.id]: {
          top: buttonRect.bottom + 5,
          right: window.innerWidth - buttonRect.right
        }
      }));
    }
    
    // Показуємо випадаючий список для цього paymentOrder
    setShowDetachDropdown(prev => ({ ...prev, [paymentOrder.id]: true }));
  };

  const handleDetachPayment = async () => {
    if (!selectedPaymentOrderToDetach) {
      return;
    }

    setIsLoading(true);
    try {
      await paymentOrdersApi.detach(selectedPaymentOrderToDetach.id);
      // Закриваємо випадаючий список
      setShowDetachDropdown(prev => ({ ...prev, [selectedPaymentOrderToDetach.id]: false }));
      setSelectedPaymentOrderToDetach(null);
      await loadAttachedPayments();
      await loadAvailablePayments(); // Оновлюємо список доступних оплат
      if (onPaymentAttached) {
        onPaymentAttached();
      }
    } catch (error) {
      setErrorMessage(getApiErrorText(error, 'Помилка відкріплення оплати'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateManualPayment = async () => {
    setCreateManualError('');
    if (!manualPaymentForm.amount || parseFloat(manualPaymentForm.amount) <= 0) {
      setCreateManualError('Сума оплати повинна бути більше 0');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      // Створюємо ручну оплату
      const requestData = {
        amount: parseFloat(manualPaymentForm.amount),
        type: 'IN',
        // Для ручних оплат не передаємо transactionDate - воно буде NULL
        adminComment: manualPaymentForm.adminComment || null
      };

      const createRes = await paymentsApi.createManual(requestData);
      
      if (createRes.status === 200 && createRes.data.data) {
        const newPayment = createRes.data.data;
        
        // Автоматично прикріплюємо створену оплату до замовлення
        await paymentOrdersApi.attach(newPayment.id, orderId, null);
        
        setSuccessMessage('Оплату успішно створено та прикріплено до замовлення');
        
        // Очищаємо форму та закриваємо модальне вікно
        setManualPaymentForm({
          amount: '',
          adminComment: ''
        });
        setCreateManualError('');
        setShowCreateManualPaymentModal(false);
        
        // Оновлюємо списки
        await loadAttachedPayments();
        await loadAvailablePayments();
        
        if (onPaymentAttached) {
          onPaymentAttached();
        }
        
        setTimeout(() => {
          setSuccessMessage('');
        }, 2000);
      }
    } catch (error) {
      setCreateManualError(getApiErrorText(error, 'Помилка створення оплати'));
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return String(dateString);
    const currentYear = new Date().getFullYear();
    const year = date.getFullYear();
    
    if (year === currentYear) {
      return date.toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
    } else {
      return date.toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    }
  };

  /** Один формат дати оплати: без секунд; рік тільки якщо не поточний. Приймає displayDate з бекенду або createdAt/transactionDate. */
  const formatPaymentDateDisplay = (value) => {
    if (!value) return '-';
    const str = typeof value === 'string' ? value : (value && value.toISOString ? value.toISOString() : String(value));
    const match = str.match(/^(\d{2})\.(\d{2})\.(\d{4}),\s*(\d{2}):(\d{2})/);
    if (match) {
      const [, d, m, y, hour, min] = match;
      const year = parseInt(y, 10);
      const currentYear = new Date().getFullYear();
      return year === currentYear ? `${d}.${m}, ${hour}:${min}` : `${d}.${m}.${y}, ${hour}:${min}`;
    }
    const date = new Date(str);
    if (Number.isNaN(date.getTime())) return str;
    const currentYear = new Date().getFullYear();
    const year = date.getFullYear();
    return year === currentYear
      ? date.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
      : date.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatPaymentDate = (createdAt, transactionDate) => {
    const value = createdAt || transactionDate;
    if (!value) return '-';
    return formatPaymentDateDisplay(value);
  };

  const formatAmount = (amount, showCurrency = true) => {
    const formatted = parseFloat(amount || 0).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return showCurrency ? `${formatted} грн` : formatted;
  };

  const getPaymentTypeDisplay = (payment) => {
    const paymentType = payment.type?.databaseValue || payment.type?.name || payment.type;
    const isIn = paymentType === "IN" || paymentType === "INCOMING";
    return isIn ? "Вхідний" : "Вихідний";
  };

  const getPaymentDetails = (payment) => {
    const parts = [];
    if (payment.bankAccountName) parts.push(payment.bankAccountName);
    if (payment.bankDescription) parts.push(payment.bankDescription);
    if (payment.counterparty) parts.push(payment.counterparty);
    return parts.join(' ');
  };

  const getPaymentFullText = (payment) => {
    // Створюємо суцільний текст для пошуку: тип, дата, деталі, сума
    const type = getPaymentTypeDisplay(payment);
    // Використовуємо created_at для відображення (для ручних оплат transaction_date = NULL)
    const date = formatPaymentDate(payment.transactionDate, payment.createdAt);
    const details = getPaymentDetails(payment);
    // Додаємо суму в різних форматах для пошуку (з пробілами, без пробілів, з комою, з крапкою)
    const amount = payment.amount ? payment.amount.toString() : '';
    const amountFormatted = formatAmount(payment.amount);
    return `${type} ${date} ${details} ${amount} ${amountFormatted}`.toLowerCase();
  };

  const filterPayments = (payments, query) => {
    if (!query || query.trim() === '') {
      return payments;
    }
    const lowerQuery = query.toLowerCase();
    return payments.filter(payment => {
      const fullText = getPaymentFullText(payment);
      return fullText.includes(lowerQuery);
    });
  };

  const filteredPayments = filterPayments(availablePayments, searchQuery);
  const selectedPayment = selectedPaymentId ? availablePayments.find(p => p.id === parseInt(selectedPaymentId)) : null;

  // Розраховуємо загальну суму прикріплених оплат
  const totalAttachedAmount = attachedPayments.reduce((sum, po) => {
    return sum + (parseFloat(po.amount) || 0);
  }, 0);

  // Перевіряємо, чи замовлення повністю оплачене
  const isOrderFullyPaid = orderPrice && totalAttachedAmount >= parseFloat(orderPrice);
  const isOrderPaid = orderPaymentStatus?.adminValue === 'Оплачений';
  const isMobileCollapsible = isMobile && isOrderPaid;

  return (
    <div className="card mb-3">
      <div
        className={isMobileCollapsible ? 'card-header d-flex justify-content-between align-items-center cursor-pointer' : 'card-header'}
        onClick={isMobileCollapsible ? () => setPaymentsOpened(prev => !prev) : undefined}
      >
        {isMobileCollapsible ? (
          <>
            <h5 className='card-title font-weight-bold m-0'>Прикріплені оплати: </h5>
            <span className="text-muted font-weight-bold">{paymentsOpened ? '−' : '+'}</span>
          </>
        ) : (
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <h5 className='card-title font-weight-bold m-0'>Прикріплені оплати: </h5>
            {orderPrice && !(orderPaymentStatus?.adminValue === 'Оплачений' && attachedPayments.length === 0) && totalAttachedAmount > 0 && !isOrderFullyPaid && (
              <span className="badge badge-warning mt-2 mt-md-0">Частково оплачено</span>
            )}
          </div>
        )}
      </div>
      {(!isMobileCollapsible || paymentsOpened) && (
      <div className="card-body">
        {/* Оплачено / вся сума; другий рядок — залишилось (тільки при частковій оплаті) */}
        {orderPrice && totalAttachedAmount > 0 && !isOrderFullyPaid && (
          <div className="mb-3">
            <div className="d-flex flex-wrap align-items-center mb-1">
              <span className="text-muted font-weight-bold mr-2">Оплачено:</span>
              <span className="text-success mr-2">{formatAmount(totalAttachedAmount)}</span>
              <span className="text-muted mr-2">/ {formatAmount(orderPrice)}</span>
            </div>
            <div className="d-flex flex-wrap align-items-baseline">
              <span className="text-muted font-weight-bold mr-2">Залишилось:</span>
              <span className="text-warning">
                {formatAmount(Math.max(0, parseFloat(orderPrice) - totalAttachedAmount))}
              </span>
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="alert alert-danger" role="alert">
            {errorMessage}
          </div>
        )}
        {successMessage && (
          <div className="alert alert-success" role="alert">
            {successMessage}
          </div>
        )}

        {attachedPayments.length > 0 ? (
          isMobile ? (
            /* Мобайл: 3 колонки — Дата, Деталі (ID+рахунок, опис, сума), Дії */
            <table className="table table-striped table-bordered table-hover table-sm mb-3">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Деталі</th>
                    <th>Дії</th>
                  </tr>
                </thead>
                <tbody>
                  {attachedPayments.map((po) => (
                    <tr key={po.id}>
                      <td className="align-top">{formatPaymentDateDisplay(po.payment?.displayDate || po.payment?.createdAt || po.payment?.transactionDate)}</td>
                      <td className="align-top">
                        {po.payment?.isManual ? (
                          <>
                            <div>
                              <span className="text-muted font-weight-bold mr-2">#{po.payment?.id || po.paymentId}</span>
                              <span className="badge badge-dark mr-2" style={{ fontSize: '0.75rem' }}>Ручна оплата</span>
                            </div>
                            {po.payment?.adminComment && (
                              <div className="text-muted mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                                <i className="bi bi-chat-left-text mr-1"></i>
                                {po.payment.adminComment}
                              </div>
                            )}
                            <div className="mt-2 font-weight-bold">{formatAmount(po.amount)}</div>
                          </>
                        ) : (
                          <>
                            <div>
                              <span className="text-muted font-weight-bold mr-2">#{po.payment?.id || po.paymentId}</span>
                              <span className="mr-2 align-middle" style={{ lineHeight: 1 }}>
                                {po.payment?.bankType?.databaseValue === "PRIVATBANK" && (
                                  <img src={privatbankIcon} alt='PrivatBank' style={{ width: '16px', height: '16px', verticalAlign: 'middle' }} />
                                )}
                                {po.payment?.bankType?.databaseValue === "MONOBANK" && (
                                  <img src={monobankIcon} alt='Monobank' style={{ width: '16px', height: '16px', verticalAlign: 'middle' }} />
                                )}
                              </span>
                              <span className="font-weight-bold">{po.payment?.bankAccountName ?? po.payment?.bankAccount?.name ?? po.payment?.bankAccountId ?? '-'}</span>
                            </div>
                            {po.payment?.bankDescription && (
                              <div className="mt-1">{po.payment.bankDescription}</div>
                            )}
                            <div className="mt-2">
                              {po.payment?.amount != null && parseFloat(po.amount) < parseFloat(po.payment.amount) ? (
                                <span title="Частково використано з платежу">
                                  <span className="font-weight-bold">{formatAmount(po.amount, false)}</span> / {formatAmount(po.payment.amount)}
                                </span>
                              ) : (
                                <span className="font-weight-bold">{formatAmount(po.amount)}</span>
                              )}
                            </div>
                          </>
                        )}
                      </td>
                      <td className="align-top">
                        <div className="position-relative payment-detach-dropdown-container" style={{ display: 'inline-block' }}>
                          <i
                            role="button"
                            tabIndex={isLoading ? -1 : 0}
                            className="bi bi-trash-fill text-danger cursor-pointer"
                            style={{ fontSize: '1.1rem', opacity: isLoading ? 0.5 : 1, pointerEvents: isLoading ? 'none' : 'auto' }}
                            onClick={(e) => { if (!isLoading) handleOpenDetachDropdown(po, e); }}
                            onKeyDown={(e) => { if (!isLoading && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleOpenDetachDropdown(po, e); } }}
                            aria-label="Відкріпити"
                            title="Відкріпити"
                          />
                          {showDetachDropdown[po.id] && selectedPaymentOrderToDetach?.id === po.id && (
                            <div 
                              className="position-fixed bg-white border rounded shadow-lg p-3"
                              style={{
                                zIndex: 9999,
                                width: '300px',
                                top: detachDropdownPosition[po.id]?.top ? `${detachDropdownPosition[po.id].top}px` : 'auto',
                                right: detachDropdownPosition[po.id]?.right ? `${detachDropdownPosition[po.id].right}px` : 'auto',
                              }}
                            >
                              <p className="mb-3">Ви впевнені, що хочете відкріпити цю оплату від замовлення?</p>
                              <button
                                type="button"
                                className="btn btn-dark btn-sm btn-block"
                                onClick={handleDetachPayment}
                                disabled={isLoading}
                              >
                                Підтвердити
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
          ) : (
            /* Десктоп: 4 колонки */
            <table className="table table-striped table-bordered table-hover table-sm mb-3">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Опис</th>
                  <th>Сума</th>
                  <th>Дії</th>
                </tr>
              </thead>
              <tbody>
                {attachedPayments.map((po) => (
                  <tr key={po.id}>
                    <td>{formatPaymentDateDisplay(po.payment?.displayDate || po.payment?.createdAt || po.payment?.transactionDate)}</td>
                    <td>
                      {po.payment?.isManual ? (
                        <>
                          <div>
                            <span className="text-muted font-weight-bold mr-2">#{po.payment?.id || po.paymentId}</span>
                            <span className="badge badge-dark mr-2" style={{ fontSize: '0.75rem' }}>Ручна оплата</span>
                          </div>
                          {po.payment?.adminComment && (
                            <div className="text-muted mt-1" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                              <i className="bi bi-chat-left-text mr-1"></i>
                              {po.payment.adminComment}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div>
                            <span className="text-muted font-weight-bold mr-2">#{po.payment?.id || po.paymentId}</span>
                            <span className="mr-2 align-middle" style={{ lineHeight: 1 }}>
                              {po.payment?.bankType?.databaseValue === "PRIVATBANK" && (
                                <img src={privatbankIcon} alt='PrivatBank' style={{ width: '16px', height: '16px', verticalAlign: 'middle' }} />
                              )}
                              {po.payment?.bankType?.databaseValue === "MONOBANK" && (
                                <img src={monobankIcon} alt='Monobank' style={{ width: '16px', height: '16px', verticalAlign: 'middle' }} />
                              )}
                            </span>
                            <span className="font-weight-bold">{po.payment?.bankAccountName ?? po.payment?.bankAccount?.name ?? po.payment?.bankAccountId ?? '-'}</span>
                          </div>
                          {po.payment?.bankDescription && (
                            <div className="mt-1">{po.payment.bankDescription}</div>
                          )}
                        </>
                      )}
                    </td>
                    <td>
                      {po.payment?.amount != null && parseFloat(po.amount) < parseFloat(po.payment.amount) ? (
                        <span title="Частково використано з платежу">
                          <span className="font-weight-bold">{formatAmount(po.amount, false)}</span> / {formatAmount(po.payment.amount)}
                        </span>
                      ) : (
                        formatAmount(po.amount)
                      )}
                    </td>
                    <td>
                      <div className="position-relative payment-detach-dropdown-container" style={{ display: 'inline-block' }}>
                        <i
                          role="button"
                          tabIndex={isLoading ? -1 : 0}
                          className="bi bi-trash-fill text-danger cursor-pointer"
                          style={{ fontSize: '1.1rem', opacity: isLoading ? 0.5 : 1, pointerEvents: isLoading ? 'none' : 'auto' }}
                          onClick={(e) => { if (!isLoading) handleOpenDetachDropdown(po, e); }}
                          onKeyDown={(e) => { if (!isLoading && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleOpenDetachDropdown(po, e); } }}
                          aria-label="Відкріпити"
                          title="Відкріпити"
                        />
                        {showDetachDropdown[po.id] && selectedPaymentOrderToDetach?.id === po.id && (
                          <div 
                            className="position-fixed bg-white border rounded shadow-lg p-3"
                            style={{
                              zIndex: 9999,
                              width: '300px',
                              top: detachDropdownPosition[po.id]?.top ? `${detachDropdownPosition[po.id].top}px` : 'auto',
                              right: detachDropdownPosition[po.id]?.right ? `${detachDropdownPosition[po.id].right}px` : 'auto',
                            }}
                          >
                            <p className="mb-3">Ви впевнені, що хочете відкріпити цю оплату від замовлення?</p>
                            <button
                              type="button"
                              className="btn btn-dark btn-sm btn-block"
                              onClick={handleDetachPayment}
                              disabled={isLoading}
                            >
                              Підтвердити
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : null}

        {!isOrderFullyPaid && (
          <>
            <div className="mb-3">
              <button
                type="button"
                className="btn btn-dark btn-sm mb-3"
                onClick={() => { setShowCreateManualPaymentModal(true); setCreateManualError(''); }}
                style={{ fontSize: '0.875rem', borderRadius: 0 }}
              >
                <i className="bi bi-plus-circle mr-1"></i>
                Додати оплату вручну
              </button>
              <div>
                <label className="mb-1" style={{ fontSize: '0.875rem' }}>Або вибрати з транзакцій:</label>
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control"
                    style={{ fontSize: '0.875rem', padding: '0.25rem 0 0.25rem 0', paddingRight: '30px', height: 'auto', borderTop: 'none', borderLeft: 'none', borderRight: 'none', borderRadius: 0, borderBottom: '1px solid #ced4da' }}
                    value={selectedPayment ? (() => {
                      const date = formatPaymentDate(selectedPayment.transactionDate, selectedPayment.createdAt);
                      const isPartiallyUsed =
                        (selectedPayment.status?.databaseValue === 'PARTIALLY_USED' ||
                          selectedPayment.status === 'PARTIALLY_USED') && selectedPayment.availableAmount;
                      const amountDisplay = isPartiallyUsed
                        ? `${formatAmount(selectedPayment.availableAmount, false)} / ${formatAmount(selectedPayment.amount, false)} грн`
                        : formatAmount(selectedPayment.amount);

                      // Окреме відображення для ручних оплат, щоб не було "null,"
                      if (selectedPayment.isManual) {
                        const adminComment = selectedPayment.adminComment || '';
                        return `Ручна оплата, ${date}${adminComment ? ` - ${adminComment}` : ''} - ${amountDisplay}`;
                      }

                      const bankAccount = selectedPayment.bankAccountName || selectedPayment.bankAccountId || '';
                      const description = selectedPayment.bankDescription || '';
                      const counterparty = selectedPayment.counterparty || '';
                      const details = [description, counterparty].filter(Boolean).join(', ');

                      return `${bankAccount}${bankAccount ? ', ' : ''}${date}${details ? ` - ${details}` : ''} - ${amountDisplay}`;
                    })() : searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowDropdown(true);
                      if (selectedPaymentId) {
                        setSelectedPaymentId(null);
                      }
                    }}
                    onFocus={() => setShowDropdown(true)}
                    placeholder="Введіть текст для пошуку або виберіть оплату"
                  />
            <span
              className="position-absolute"
              style={{
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                fontSize: "0.875rem",
                color: "#6c757d",
                zIndex: 10,
                pointerEvents: selectedPaymentId || searchQuery ? 'auto' : 'none'
              }}
              onClick={() => {
                if (selectedPaymentId || searchQuery) {
                  setSelectedPaymentId(null);
                  setSearchQuery('');
                  setShowDropdown(false);
                } else {
                  setShowDropdown(true);
                }
              }}
            >
              {selectedPaymentId || searchQuery ? '×' : <i className="bi bi-chevron-down"></i>}
            </span>
            {showDropdown && (
              <div 
                className="position-absolute w-100 bg-white border rounded shadow-lg"
                style={{ 
                  zIndex: 1000, 
                  maxHeight: "300px", 
                  overflowY: "auto",
                  top: "100%",
                  marginTop: "2px"
                }}
                onMouseDown={(e) => e.preventDefault()} // Запобігаємо закриттю при кліку всередині
              >
                {isLoading ? (
                  <div className="p-3 text-center">Завантаження...</div>
                ) : filteredPayments.length > 0 ? (
                  filteredPayments.map((payment) => {
                    return (
                      <div
                        key={payment.id}
                        onClick={() => {
                          setSelectedPaymentId(payment.id.toString());
                          setSearchQuery('');
                          setShowDropdown(false);
                        }}
                        style={{
                          padding: "10px",
                          cursor: "pointer",
                          borderBottom: "1px solid #dee2e6"
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f8f9fa"}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "white"}
                      >
                        <div style={{ fontSize: "0.875rem" }}>
                          {payment.isManual ? (
                            <>
                              <span className="badge badge-dark mr-2" style={{ fontSize: '0.75rem' }}>Ручна оплата</span>
                              <span className="font-weight-bold">{formatPaymentDate(payment.transactionDate, payment.createdAt)}</span>
                            </>
                          ) : (
                            <>
                              <span className="font-weight-bold">
                                {payment.bankType?.databaseValue === "PRIVATBANK" && (
                                  <img 
                                    src={privatbankIcon} 
                                    alt='PrivatBank' 
                                    style={{ width: '16px', height: '16px', marginRight: '4px', verticalAlign: 'middle' }}
                                  />
                                )}
                                {payment.bankType?.databaseValue === "MONOBANK" && (
                                  <img 
                                    src={monobankIcon} 
                                    alt='Monobank' 
                                    style={{ width: '16px', height: '16px', marginRight: '4px', verticalAlign: 'middle' }}
                                  />
                                )}
                                {payment.bankAccountName || payment.bankAccountId}
                              </span>
                              <span className="ml-2">{formatPaymentDate(payment.transactionDate, payment.createdAt)}</span>
                            </>
                          )}
                        </div>
                        {payment.isManual ? (
                          <div style={{ fontSize: "0.875rem", color: "#6c757d", marginTop: "4px" }}>
                            {payment.adminComment && (
                              <span>
                                <i className="bi bi-chat-left-text mr-1"></i>
                                {payment.adminComment}
                              </span>
                            )}
                          </div>
                        ) : (
                          <>
                            <div style={{ fontSize: "0.875rem", color: "#6c757d" }}>
                              {payment.bankDescription && <span>{payment.bankDescription}</span>}
                              {payment.counterparty && <span className="ml-2">{payment.counterparty}</span>}
                            </div>
                            {payment.adminComment && (
                              <div style={{ fontSize: "0.875rem", color: "#6c757d", marginTop: "4px" }}>
                                <i className="bi bi-chat-left-text mr-1"></i>
                                {payment.adminComment}
                              </div>
                            )}
                          </>
                        )}
                        <div style={{ fontSize: "0.875rem", fontWeight: "bold" }}>
                          {(payment.status?.databaseValue === 'PARTIALLY_USED' || payment.status === 'PARTIALLY_USED') && payment.availableAmount ? (
                            <span>
                              <span style={{ color: "#28a745" }}>+{formatAmount(payment.availableAmount, false)}</span>
                              <span style={{ color: "#ffc107" }}> / {formatAmount(payment.amount, false)} грн</span>
                            </span>
                          ) : (
                            <span style={{ color: "#28a745" }}>+{formatAmount(payment.amount)}</span>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="p-3 text-center text-muted">
                    {searchQuery ? 'Нічого не знайдено' : 'Немає доступних оплат'}
                  </div>
                )}
              </div>
            )}
                </div>
              </div>
            </div>

        {selectedPaymentId && (
          <button
            type="button"
            className="btn btn-dark btn-block mt-3"
            onClick={handleAttachPayment}
            disabled={isLoading}
            style={{ fontSize: '0.875rem' }}
          >
            Зберегти
          </button>
        )}
          </>
        )}

      </div>
      )}

      {/* Модальне вікно для створення ручної оплати */}
      {showCreateManualPaymentModal && (
        <div 
          className="modal fade show" 
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabIndex="-1" 
          role="dialog"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCreateManualPaymentModal(false);
            }
          }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title font-weight-bold">Додати оплату вручну</h5>
                <button 
                  type="button" 
                  className="close" 
                  onClick={() => { setShowCreateManualPaymentModal(false); setCreateManualError(''); }}
                  aria-label="Close"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                {createManualError && (
                  <div className="alert alert-danger" role="alert">
                    {createManualError}
                  </div>
                )}
                <div className="form-group">
                  <label className="font-weight-bold">Сума *</label>
                  <input
                    type="number"
                    className="form-control"
                    step="0.01"
                    min="0.01"
                    value={manualPaymentForm.amount}
                    onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, amount: e.target.value })}
                    placeholder="Введіть суму"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="font-weight-bold">Коментар адміна</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={manualPaymentForm.adminComment}
                    onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, adminComment: e.target.value })}
                    placeholder="Введіть коментар (наприклад, від кого оплата)"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-dark w-100" 
                  onClick={handleCreateManualPayment}
                  disabled={isLoading}
                >
                  Створити та прикріпити
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttachPaymentToOrder;
